/**
 * Sidebar Navigation Component
 * Responsive sidebar with mobile support
 */

import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ChartPie,
  Briefcase,
  Calculator,
  Wrench,
  ChartBar,
  SignOut,
  Buildings,
  Users,
  Gear,
  CaretRight,
  Brain,
  Database,
  Question,
  UserCircle,
  UsersThree,
  X,
  Gavel,
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const navItems = [
  { 
    path: '/', 
    label: 'Vue d\'ensemble', 
    shortLabel: 'Aperçu',
    icon: ChartPie,
  },
  { 
    path: '/pipeline', 
    label: 'Pipeline des Deals', 
    shortLabel: 'Pipeline',
    icon: Briefcase,
  },
  { 
    path: '/analyzer', 
    label: 'Analyseur Rapide', 
    shortLabel: 'Analyseur',
    icon: Calculator,
  },
  { 
    path: '/renovation', 
    label: 'Suivi Rénovation', 
    shortLabel: 'Rénovation',
    icon: Wrench,
  },
  { 
    path: '/comparables', 
    label: 'Comparables Marché', 
    shortLabel: 'Comparables',
    icon: Buildings,
  },
  { 
    path: '/exit', 
    label: 'Suivi de Sortie', 
    shortLabel: 'Sortie',
    icon: ChartBar,
  },
  { 
    path: '/partners', 
    label: 'Tableau Partenaires', 
    shortLabel: 'Partenaires',
    icon: Users,
  },
  { 
    path: '/intelligence', 
    label: 'Intelligence IA', 
    shortLabel: 'IA',
    icon: Brain,
  },
  { 
    path: '/data-ingestion', 
    label: 'Import de Données', 
    shortLabel: 'Données',
    icon: Database,
  },
  { 
    path: '/licitor', 
    label: 'Enchères Licitor', 
    shortLabel: 'Enchères',
    icon: Gavel,
  },
];

const bottomNavItems = [
  { 
    path: '/help', 
    label: 'Aide & Docs', 
    shortLabel: 'Aide',
    icon: Question,
  },
  { 
    path: '/settings', 
    label: 'Paramètres', 
    shortLabel: 'Paramètres',
    icon: Gear,
  },
];

export const Sidebar = ({ isMobile = false, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  // Get user initials
  const userInitials = user?.full_name 
    ? user.full_name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path || 
      (item.path !== '/' && location.pathname.startsWith(item.path));

    return (
      <NavLink
        to={item.path}
        onClick={handleNavClick}
        data-testid={`nav-${item.path.replace('/', '') || 'overview'}`}
        className={cn(
          'sidebar-nav-item group',
          isActive ? 'sidebar-nav-item-active' : 'sidebar-nav-item-inactive'
        )}
      >
        <Icon 
          size={20} 
          weight={isActive ? 'fill' : 'regular'}
          className="flex-shrink-0"
        />
        <span className="flex-1 truncate">{item.label}</span>
        {isActive && (
          <CaretRight size={14} weight="bold" className="opacity-60 flex-shrink-0" />
        )}
      </NavLink>
    );
  };

  return (
    <aside 
      data-testid="sidebar"
      className={cn(
        "h-screen bg-[#1C1C1A] flex flex-col",
        isMobile ? "w-full" : "fixed left-0 top-0 w-[260px] z-50"
      )}
    >
      {/* Logo / Brand */}
      <div className="p-4 sm:p-6 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#3A4A40] to-[#2E5F43] flex items-center justify-center flex-shrink-0">
            <Buildings size={20} weight="fill" className="text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-white font-semibold text-sm tracking-tight truncate">
              Deal Intelligence
            </h1>
            <p className="text-white/40 text-xs">Paris RE Platform</p>
          </div>
        </div>
        
        {/* Mobile close button */}
        {isMobile && (
          <button 
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 p-3 sm:p-4 overflow-y-auto custom-scrollbar">
        <div className="space-y-1">
          {navItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </div>
        
        {/* Admin Section */}
        {isAdmin && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-white/30 text-xs uppercase tracking-wider px-3 mb-2">Admin</p>
            <NavItem 
              item={{ 
                path: '/users', 
                label: 'Gestion Utilisateurs', 
                shortLabel: 'Utilisateurs',
                icon: UsersThree 
              }} 
            />
          </div>
        )}
      </nav>

      {/* Bottom Navigation */}
      <div className="p-3 sm:p-4 border-t border-white/10">
        <div className="space-y-1">
          {bottomNavItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </div>
        
        {/* User Profile Link */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <NavLink
            to="/profile"
            onClick={handleNavClick}
            data-testid="nav-profile"
            className={cn(
              'sidebar-nav-item group',
              location.pathname === '/profile' ? 'sidebar-nav-item-active' : 'sidebar-nav-item-inactive'
            )}
          >
            <div className="w-6 h-6 rounded-full bg-[#3A4A40] flex items-center justify-center text-xs text-white font-medium flex-shrink-0">
              {userInitials}
            </div>
            <span className="flex-1 truncate">{user?.full_name || 'Mon Compte'}</span>
          </NavLink>
          
          <button 
            onClick={() => { handleNavClick(); handleLogout(); }}
            data-testid="signout-btn"
            className="sidebar-nav-item sidebar-nav-item-inactive w-full mt-1"
          >
            <SignOut size={20} />
            <span className="flex-1">Déconnexion</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
